import { useState } from "react";
import ScreenHeader from "@/components/mnf/ScreenHeader";
import Icon from "@/components/mnf/Icon";
import { Link, useLocation } from "wouter";
import { GlassCard, PillTag, Btn, Avatar, SectionTitle } from "@/components/mnf/UI";
import { UpsellCard } from "@/components/mnf/TierLock";
import { useStoreQuery, queries, tierAtLeast } from "@/lib/store";
import StrengthCard from "@/components/mnf/StrengthCard";
import SabbathToggle from "@/components/mnf/SabbathToggle";
import DisciplineBadgesCard from "@/components/mnf/DisciplineBadgesCard";

const prayers = [
  {
    sister: "Sister Sarah", time: "2 hours ago", initials: "SS",
    body: "Asking for strength as I navigate a difficult transition at work. Praying for peace that transcends understanding during the long hours.",
    waters: 14, accent: "coral" as const,
  },
  {
    sister: "Sister Maria", time: "5 hours ago", initials: "SM",
    body: '"The Lord is near to the brokenhearted." Celebrating my daughter\'s recovery but asking for continued healing prayers from my sisters.',
    waters: 32, accent: "purple" as const,
  },
  {
    sister: "Sister Joy",   time: "8 hours ago", initials: "SJ",
    body: "Starting a new wellness journey today. Praying for discipline and a heart that seeks to nourish this temple with grace, alongside my sisters.",
    waters: 47, accent: "forest" as const,
  },
  {
    sister: "Anonymous Sister", time: "yesterday", initials: "AS",
    body: "For my household, beloved. We are in a season of drought. Praying for rain and restoration.",
    waters: 108, accent: "gold" as const,
  },
];

const accentMap = {
  coral: { bg: "rgba(192,120,74,0.10)", text: "var(--mnf-t)" },
  purple: { bg: "var(--mnf-purpleL)", text: "var(--mnf-purple)" },
  forest: { bg: "rgba(30,74,53,0.06)", text: "var(--mnf-f)" },
  gold:   { bg: "var(--mnf-gold2)", text: "var(--mnf-gold)" },
};

const inputStyle = {
  width: "100%",
  padding: "9px 12px",
  borderRadius: 12,
  border: "1px solid rgba(30,74,53,0.14)",
  background: "rgba(255,255,255,0.85)",
  fontSize: 13,
  color: "var(--mnf-fd)" as string,
  outline: "none",
  fontFamily: "inherit",
};

export default function Flourish() {
  const me = useStoreQuery(queries.currentUser);
  // No fallback to the seeded demo account: SabbathToggle and DisciplineBadgesCard below
  // both read AND write per-user state. See the early return below.
  const userId = me?.id ?? null;
  const [, setLocation] = useLocation();
  const isFlourishing = tierAtLeast(me?.tier, "Flourishing");

  // Daily Grace check-in
  const [selectedMood, setSelectedMood] = useState<string | null>(null);
  const [moodFlash, setMoodFlash] = useState(false);

  // Prayer compose
  const [prayerText, setPrayerText] = useState("");
  const [sharedFlash, setSharedFlash] = useState(false);
  const [aiFlash, setAiFlash] = useState(false);

  // Prayer wall
  const [waterCounts, setWaterCounts] = useState<Record<number, number>>(
    () => Object.fromEntries(prayers.map((p, i) => [i, p.waters])),
  );
  const [watered, setWatered] = useState<Set<number>>(new Set());
  const [seedOpen, setSeedOpen] = useState<number | null>(null);
  const [seedTexts, setSeedTexts] = useState<Record<number, string>>({});
  const [seedSown, setSeedSown] = useState<Set<number>>(new Set());

  const handleMood = (mood: string) => {
    setSelectedMood(mood);
    setMoodFlash(true);
    setTimeout(() => setMoodFlash(false), 2400);
  };

  const handleShareWisdom = () => {
    if (!prayerText.trim()) return;
    setSharedFlash(true);
    setPrayerText("");
    setTimeout(() => setSharedFlash(false), 2400);
  };

  const handleAiFill = () => {
    const suggestions = [
      "Heavenly Father, still my anxious heart and remind me that you hold all things in your hands.",
      "Lord, I need your strength today. Let my weakness become a vessel for your grace.",
      "Father, let your peace that passes understanding guard my heart and mind today.",
    ];
    const pick = suggestions[Math.floor(Math.random() * suggestions.length)];
    setPrayerText(pick);
    setAiFlash(true);
    setTimeout(() => setAiFlash(false), 1600);
  };

  const handleWater = (idx: number) => {
    if (watered.has(idx)) return;
    setWatered((prev) => { const s = new Set(prev); s.add(idx); return s; });
    setWaterCounts((prev) => ({ ...prev, [idx]: prev[idx] + 1 }));
  };

  const handleSeedSow = (idx: number) => {
    if (!seedTexts[idx]?.trim()) return;
    setSeedSown((prev) => { const s = new Set(prev); s.add(idx); return s; });
    setSeedOpen(null);
    setSeedTexts((prev) => ({ ...prev, [idx]: "" }));
  };

  if (!userId) {
    return (
      <div className="px-5 pt-6">
        <p className="mnf-body" style={{ color: "var(--mnf-tg)" }}>
          Sign in to see your Flourish disciplines, sister.
        </p>
      </div>
    );
  }

  return (
    <>
      <ScreenHeader
        eyebrow="Spiritual disciplines"
        title="Flourish"
        subtitle={
          <>"Like a tree planted by streams of water, that yields its fruit in its season." Psalm 1:3</>
        }
        trailing={
          <Link
            href="/share"
            className="flex items-center justify-center hover-elevate"
            style={{ width: 36, height: 36, borderRadius: "50%", background: "rgba(30,74,53,0.08)", color: "var(--mnf-f)", border: "1px solid rgba(30,74,53,0.06)" }}
            aria-label="Share grace"
          >
            <Icon name="ios_share" size={18} />
          </Link>
        }
      />

      <div className="px-5 pt-1 space-y-4">
        {/* Monthly Strength Card */}
        <StrengthCard />

        {/* Sabbath rest day */}
        <SabbathToggle userId={userId} />

        {/* Spiritual discipline streak badges */}
        <DisciplineBadgesCard userId={userId} variant="full" />

        {/* Direct line to Coach Kiley */}
        {isFlourishing ? (
          <Link href="/kiley" className="no-underline block" data-testid="link-kiley-thread">
            <GlassCard
              organic
              padding={16}
              className="mnf-fade-up"
              style={{
                background: "linear-gradient(135deg, rgba(160,120,32,0.12) 0%, rgba(245,240,232,0.7) 100%)",
                border: "1px solid rgba(160,120,32,0.30)",
              }}
            >
              <div className="flex items-center gap-3">
                <Avatar initials="K" size={44} ringColor="rgba(160,120,32,0.45)" />
                <div className="flex-1 min-w-0">
                  <p className="mnf-eyebrow" style={{ color: "var(--mnf-gold)" }}>Inner circle</p>
                  <p
                    className="mnf-serif"
                    style={{ fontSize: 17, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.25, marginTop: 2 }}
                  >
                    Direct line to Coach Kiley
                  </p>
                  <p className="mnf-body italic" style={{ fontSize: 12, color: "var(--mnf-tg)", marginTop: 3 }}>
                    Open thread, ask anything
                  </p>
                </div>
                <span
                  className="flex items-center justify-center shrink-0"
                  style={{
                    width: 36, height: 36, borderRadius: 999,
                    background: "var(--mnf-gold)", color: "#fff",
                  }}
                >
                  <Icon name="forum" size={17} weight={500} />
                </span>
              </div>
            </GlassCard>
          </Link>
        ) : (
          <UpsellCard
            tier="Flourishing"
            eyebrow="Inner circle"
            title="Direct line to Coach Kiley"
            description="Flourishing sisters can message Kiley one-on-one for prayer, form checks, or whatever your heart needs to ask."
            ctaLabel="Bloom into Flourishing"
            onCta={() => setLocation("/kiley")}
            icon="forum"
          />
        )}

        {/* Daily Grace check-in */}
        <GlassCard padding={18} className="mnf-fade-up">
          <p className="mnf-eyebrow">Daily grace check-in</p>
          <p className="mnf-serif mt-1" style={{ fontSize: 18, fontWeight: 500, color: "var(--mnf-fd)" }}>
            How is your spirit, beloved?
          </p>
          <div className="grid grid-cols-3 gap-2 mt-3">
            {([
              { l: "Radiant",   icon: "wb_sunny",     tone: { bg: "var(--mnf-gold2)",  text: "var(--mnf-gold)" } },
              { l: "Resting",   icon: "bedtime",      tone: { bg: "var(--mnf-purpleL)", text: "var(--mnf-purple)" } },
              { l: "Restoring", icon: "favorite",     tone: { bg: "rgba(192,120,74,0.10)", text: "var(--mnf-t)" } },
            ]).map((m) => {
              const isSelected = selectedMood === m.l;
              return (
                <button
                  key={m.l}
                  type="button"
                  data-testid={`mood-${m.l.toLowerCase()}`}
                  onClick={() => handleMood(m.l)}
                  className="flex flex-col items-center gap-1.5 py-3 hover-elevate transition-all"
                  style={{
                    background: m.tone.bg,
                    borderRadius: 16,
                    border: isSelected
                      ? `2px solid ${m.tone.text}`
                      : "1px solid rgba(30,74,53,0.04)",
                    cursor: "pointer",
                    transform: isSelected ? "scale(1.03)" : "scale(1)",
                  }}
                >
                  <Icon name={m.icon} size={22} color={m.tone.text} filled />
                  <span className="mnf-body text-xs uppercase" style={{ color: m.tone.text, fontWeight: 700, letterSpacing: "0.1em" }}>
                    {m.l}
                  </span>
                </button>
              );
            })}
          </div>
          {moodFlash && selectedMood && (
            <p
              className="mnf-body italic text-center mt-3"
              style={{ fontSize: 12.5, color: "var(--mnf-tg)" }}
            >
              Your spirit has been heard, beloved. 🌿
            </p>
          )}
          {!moodFlash && selectedMood && (
            <p
              className="mnf-body text-center mt-3"
              style={{ fontSize: 12, color: "var(--mnf-tg)" }}
            >
              Feeling <span style={{ fontWeight: 700, color: "var(--mnf-fd)" }}>{selectedMood}</span> today.
            </p>
          )}
        </GlassCard>

        {/* Compose */}
        <GlassCard padding={16} className="mnf-fade-up">
          <div className="flex items-center gap-2 mb-2">
            <span style={{ fontSize: 16 }}>🙏</span>
            <p className="mnf-body text-xs uppercase" style={{ color: "var(--mnf-f)", fontWeight: 700, letterSpacing: "0.12em" }}>
              Plant a seed of prayer
            </p>
          </div>
          <textarea
            value={prayerText}
            onChange={(e) => setPrayerText(e.target.value)}
            placeholder="Where does your heart need watering today, sister?"
            rows={3}
            className="w-full mnf-body"
            style={{
              background: "rgba(255,255,255,0.55)",
              border: "1px solid rgba(30,74,53,0.06)",
              borderRadius: 14,
              padding: 12,
              fontSize: 13,
              color: "var(--mnf-tx)",
              resize: "none",
              outline: "none",
            }}
          />
          <div className="flex items-center justify-between mt-2.5">
            <div className="flex gap-1.5">
              <button
                type="button"
                aria-label="Add image"
                className="flex items-center justify-center hover-elevate"
                style={{ width: 32, height: 32, borderRadius: 10, background: "rgba(30,74,53,0.06)", color: "var(--mnf-f)", border: "none", cursor: "pointer" }}
                onClick={() => {}}
              >
                <Icon name="image" size={16} />
              </button>
              <button
                type="button"
                aria-label="AI prayer suggestion"
                className="flex items-center justify-center hover-elevate"
                style={{
                  width: 32, height: 32, borderRadius: 10,
                  background: aiFlash ? "var(--mnf-f)" : "rgba(30,74,53,0.06)",
                  color: aiFlash ? "#F5F0E8" : "var(--mnf-f)",
                  border: "none", cursor: "pointer",
                  transition: "background 180ms",
                }}
                onClick={handleAiFill}
              >
                <Icon name="auto_awesome" size={16} />
              </button>
            </div>
            <Btn
              variant="primary"
              size="sm"
              onClick={handleShareWisdom}
              disabled={!prayerText.trim()}
              data-testid="button-share-wisdom"
            >
              {sharedFlash ? "Planted 🌱" : "Share Wisdom"}
            </Btn>
          </div>
        </GlassCard>

        <SectionTitle
          eyebrow="The wall"
          title="Praying together"
          trailing={`${prayers.length} active`}
        />

        {/* Prayer cards */}
        <div className="space-y-3 pb-2">
          {prayers.map((p, i) => {
            const a = accentMap[p.accent];
            const hasWatered = watered.has(i);
            const hasSown = seedSown.has(i);
            return (
              <GlassCard key={i} padding={0} className="mnf-fade-up">
                <div className="px-4 pt-3.5 pb-2 flex items-center gap-2.5">
                  <Avatar initials={p.initials} size={36} ringColor="rgba(192,120,74,0.4)" />
                  <div>
                    <p className="mnf-body" style={{ fontSize: 13, fontWeight: 700, color: "var(--mnf-fd)" }}>
                      {p.sister}
                    </p>
                    <p className="text-[11px] mnf-body uppercase" style={{ color: "var(--mnf-tg)", letterSpacing: "0.08em" }}>
                      {p.time}
                    </p>
                  </div>
                </div>
                <div
                  className="mx-4 mb-3 p-3.5"
                  style={{
                    background: a.bg,
                    borderLeft: `3px solid ${a.text}`,
                    borderRadius: "4px 14px 14px 4px",
                  }}
                >
                  <p className="mnf-body" style={{ fontSize: 13.5, lineHeight: 1.55, color: "var(--mnf-tx)" }}>
                    {p.body}
                  </p>
                </div>
                <div className="px-4 pb-3 space-y-2.5">
                  <div className="flex items-center gap-1.5">
                    <Icon name="water_drop" size={14} color="var(--mnf-t)" filled />
                    <span className="text-[11px] mnf-body" style={{ color: "var(--mnf-tg)" }}>
                      {waterCounts[i]} watered with grace
                    </span>
                    {hasWatered && (
                      <PillTag tone="coral">You watered this</PillTag>
                    )}
                    {hasSown && (
                      <PillTag tone="forest">Seed sown 🌱</PillTag>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Btn
                      variant="ghost"
                      size="sm"
                      block
                      onClick={() => setSeedOpen(seedOpen === i ? null : i)}
                      data-testid={`button-seed-${i}`}
                    >
                      <Icon name="eco" size={14} />
                      {seedOpen === i ? "Close" : "Plant a seed"}
                    </Btn>
                    <Btn
                      variant={hasWatered ? "secondary" : "primary"}
                      size="sm"
                      block
                      onClick={() => handleWater(i)}
                      disabled={hasWatered}
                      data-testid={`button-water-${i}`}
                    >
                      <Icon name="water_drop" size={14} filled />
                      {hasWatered ? "Watered" : "Water with grace"}
                    </Btn>
                  </div>

                  {/* Plant a seed reply form */}
                  {seedOpen === i && (
                    <div className="space-y-2 pt-1">
                      <input
                        type="text"
                        value={seedTexts[i] ?? ""}
                        onChange={(e) =>
                          setSeedTexts((prev) => ({ ...prev, [i]: e.target.value }))
                        }
                        placeholder="Your prayer for her, sister…"
                        className="mnf-body"
                        style={inputStyle}
                        autoFocus
                      />
                      <div className="flex gap-2">
                        <Btn
                          variant="secondary"
                          size="sm"
                          onClick={() => setSeedOpen(null)}
                        >
                          Cancel
                        </Btn>
                        <Btn
                          variant="primary"
                          size="sm"
                          style={{ flex: 1 }}
                          onClick={() => handleSeedSow(i)}
                          disabled={!seedTexts[i]?.trim()}
                          data-testid={`button-sow-${i}`}
                        >
                          <Icon name="eco" size={13} /> Sow this seed
                        </Btn>
                      </div>
                    </div>
                  )}
                </div>
              </GlassCard>
            );
          })}
        </div>
      </div>

      {/* Floating compose */}
      <Link
        href="/share"
        className="absolute z-20 flex items-center justify-center hover-elevate"
        style={{
          right: 18, bottom: 88,
          width: 54, height: 54, borderRadius: "50%",
          background: "var(--mnf-t)",
          color: "#fff",
          boxShadow: "0 12px 28px rgba(192,120,74,0.42), 0 4px 10px rgba(0,0,0,0.10)",
          border: "1px solid rgba(255,255,255,0.30)",
        }}
        aria-label="Compose"
      >
        <Icon name="edit" size={22} />
      </Link>
    </>
  );
}
